
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useState, useCallback } from "react";
import type { SubscriptionTier } from "@/hooks/useProfile";

export { type SubscriptionTier };

export const useSubscriptionUpdate = (currentTier: SubscriptionTier, onClose: (open: boolean) => void) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);

  const resetUpdateState = useCallback(() => {
    setIsUpdating(false);
  }, []);

  const handleUpgrade = async (tier: SubscriptionTier) => {
    if (isUpdating) return;
    setIsUpdating(true);
    console.log('Starting subscription update...', { tier, currentTier });

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('User fetch error:', userError);
        throw new Error("User not found");
      }

      console.log('Updating subscription in database...');
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          subscription_tier: tier,
          subscription_updated_at: new Date().toISOString(),
          resources_used_this_month: 0
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('Database update error:', updateError);
        throw updateError;
      }

      console.log('Database update successful');
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      onClose(false);
      await new Promise(resolve => setTimeout(resolve, 50));

      await new Promise<void>(resolve => {
        toast({
          title: "Subscription Updated!",
          description: `You have successfully ${
            currentTier === tier ? 'switched to' : 
            tier === 'free' ? 'downgraded to' : 'upgraded to'
          } the ${tier} tier.`,
          onOpenChange: (open) => {
            if (!open) resolve();
          }
        });
      });

      navigate('/account');
      
    } catch (error) {
      console.error('Final error catch:', error);
      toast({
        title: "Error updating subscription",
        description: "There was a problem updating your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return { handleUpgrade, isUpdating, resetUpdateState };
};
